import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { serviceProviderService } from '../../services';
import { ServiceProviderBase } from '../../types';
import { useNotification } from '../../context/NotificationContext';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ContextMenu, { MenuAction } from '../common/ContextMenu';
import ServiceProviderTable from '../common/ServiceProviderTable';
import { IMAGES } from '../../constants/assets';
import { useProviderUpdateListener } from '../../utils/events';
import { POLLING_CONFIG } from '../../config/polling';
import GenericSidebar from '../common/GenericSidebar';
import { refreshAll } from '../../services/refreshService';
import AnalysisCostDialog from '../common/AnalysisCostDialog';
import { useApiAnalysisCost } from '../../hooks/useApiAnalysisCost';
import { useCreditInfo } from '../../hooks/useCreditInfo';

// === TYPES ===
interface DienstleisterSidebarProps {
  isWide: boolean;
  onToggleWidth: () => void;
}

interface ContextMenuState {
  mouseX: number;
  mouseY: number;
  providerId: string;
  status: string;
}

// === CONSTANTS ===
const SIDEBAR_WIDTHS = {
  compact: 426,
  wide: 800
} as const;

// === SUB-COMPONENTS (kept for local logic) ===
interface FilterControlsProps {
  searchTerm: string;
  statusFilter: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onStatusFilterChange: (event: SelectChangeEvent) => void;
}

const FilterControls: React.FC<FilterControlsProps> = ({
  searchTerm,
  statusFilter,
  onSearchChange,
  onStatusFilterChange
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
      <TextField
        size="small"
        placeholder={t('providers:sidebar.searchPlaceholder')}
        value={searchTerm}
        onChange={onSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        inputProps={{ 'data-testid': 'provider-sidebar-search-input' }}
        sx={{ width: '100%' }}
      />

      <FormControl size="small" sx={{ width: '100%' }}>
        <InputLabel>{t('common:labels.status')}</InputLabel>
        <Select
          value={statusFilter}
          label={t('common:labels.status')}
          onChange={onStatusFilterChange}
          data-testid="provider-sidebar-status-filter"
        >
          <MenuItem value="all">{t('common:status.all')}</MenuItem>
          <MenuItem value="complete">{t('common:status.complete')}</MenuItem>
          <MenuItem value="analyzing">{t('common:status.analyzing')}</MenuItem>
          <MenuItem value="open">{t('common:status.open')}</MenuItem>
          <MenuItem value="failed">{t('common:status.failed')}</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

interface AddProviderDialogProps {
  open: boolean;
  newProviderName: string;
  onClose: () => void;
  onSave: () => void;
  onNameChange: (value: string) => void;
}

const AddProviderDialog: React.FC<AddProviderDialogProps> = ({
  open,
  newProviderName,
  onClose,
  onSave,
  onNameChange
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          p: 1
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 3,
        borderBottom: '1px solid #e5e7eb'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {t('providers:dialog.addTitle')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mb: 2, mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('providers:dialog.addDescription')}
        </Typography>

        <TextField
          autoFocus
          fullWidth
          label={t('providers:dialog.nameLabel')}
          value={newProviderName}
          onChange={(e) => onNameChange(e.target.value)}
          variant="outlined"
          placeholder={t('providers:dialog.namePlaceholder')}
          sx={{ mt: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onSave();
            } else if (e.key === 'Escape') {
              onClose();
            }
          }}
          inputProps={{ 'data-testid': 'provider-sidebar-add-dialog-name-input' }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        <span
          style={{
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            gap: 4,
            color: 'primary.dark',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '1rem'
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={onClose}
            data-testid="provider-sidebar-add-dialog-cancel-button"
          >
            <CloseIcon sx={{ fontSize: '1.4rem' }} />
            {t('common:buttons.cancel')}
          </Box>
        </span>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={!newProviderName.trim()}
          sx={{
            textTransform: 'none',
            flex: 1
          }}
          data-testid="provider-sidebar-add-dialog-submit-button"
        >
          {t('providers:dialog.createButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// === MAIN COMPONENT ===
const DienstleisterSidebar: React.FC<DienstleisterSidebarProps> = ({ isWide, onToggleWidth }) => {
  const { t } = useTranslation();
  // === HOOKS ===
  const location = useLocation();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  // === CORE STATE ===
  const [serviceProviders, setServiceProviders] = useState<ServiceProviderBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // === FILTER/SORT STATE ===
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // === SELECTION STATE ===
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [showCostDialog, setShowCostDialog] = useState(false);
  const [pendingAnalysisProviderId, setPendingAnalysisProviderId] = useState<string | null>(null);

  // === DIALOG STATE ===
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [isAddProviderDialogOpen, setIsAddProviderDialogOpen] = useState(false);
  const [newProviderName, setNewProviderName] = useState('');

  // API-basierte Kostenschätzung für Bulk-Analyse
  const bulkCostEstimation = useApiAnalysisCost({
    analysisType: 'bulk',
    providerIds: selectedItems
  });

  // API-basierte Kostenschätzung für Einzelprovider-Analyse
  const singleCostEstimation = useApiAnalysisCost({
    analysisType: 'bulk',
    providerIds: pendingAnalysisProviderId ? [pendingAnalysisProviderId] : []
  });

  // Bestimme welche Kostenschätzung verwendet werden soll
  const currentCostEstimation = pendingAnalysisProviderId ? singleCostEstimation : bulkCostEstimation;
  const currentAnalysisType = pendingAnalysisProviderId ? 'single' : 'bulk';
  const currentContractCount = pendingAnalysisProviderId ? 1 : selectedItems.length;

  // 📊 Credit-Information laden
  const creditInfo = useCreditInfo();

  // Trigger Kostenschätzung wenn Dialog geöffnet wird
  useEffect(() => {
    if (showCostDialog) {
      if (pendingAnalysisProviderId) {
        singleCostEstimation.refetch();
      } else if (selectedItems.length > 0) {
        bulkCostEstimation.refetch();
      }
    }
  }, [showCostDialog, selectedItems.length, pendingAnalysisProviderId, bulkCostEstimation.refetch, singleCostEstimation.refetch]);

  // === UTILITY FUNCTIONS ===
  const refreshServiceProviders = async () => {
    const providers = await serviceProviderService.getServiceProviders();
    setServiceProviders(providers);
  };

  // === EVENT HANDLERS ===

  // Filter/Search Handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };

  // Selection Handlers
  const handleSelectItem = (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
    event.stopPropagation();
    const newSelectedItems = event.target.checked
      ? [...selectedItems, id]
      : selectedItems.filter(item => item !== id);
    setSelectedItems(newSelectedItems);
  };

  const handleSelectAll = (checked: boolean) => {
    const filteredIds = serviceProviders
      .filter(provider => {
        const matchesSearch = provider.title.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || provider.status === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .map(provider => provider._id);

    if (checked) {
      setSelectedItems(filteredIds);
    } else {
      setSelectedItems([]);
    }
  };

  // Context Menu Handlers
  const handleContextMenu = (event: React.MouseEvent, providerId: string, status: string) => {
    event.preventDefault();
    setSelectedRowId(providerId);
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      providerId,
      status
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedRowId(null);
  };

  // Optimistic Update für Service Provider  
  const performOptimisticProviderUpdate = useCallback((providerIds: string[]) => {
    // Sofortiges refreshAll für UI-Update
    refreshAll();

    // Nach 1,5 Sekunden nochmals refreshAll für Server-Sync
    setTimeout(() => {
      refreshAll();
    }, 1500);
  }, []);

  // Action Handlers
  const handleAnalyzeSelectedConfirmed = async () => {
    // Sofortiges UI Update
    performOptimisticProviderUpdate(selectedItems);

    try {
      const result = await serviceProviderService.analyzeAllServiceProviders(selectedItems);
      if (result.status === 'success') {
        showNotification(t('providers:messages.analyzeSuccess'), 'success');
      } else {
        showNotification(result.message, 'error');
        refreshAll(); // Bei Fehler sofort aktualisieren
      }
    } catch (err) {
      showNotification(t('providers:messages.analyzeError', { error: err }), 'error');
      refreshAll(); // Bei Fehler sofort aktualisieren
    }
  };

  const handleAnalyzeSelected = () => {
    setPendingAnalysisProviderId(null); // Bulk-Analyse
    setShowCostDialog(true);
  };

  const handleAnalyzeSingleProvider = (providerId: string) => {
    setPendingAnalysisProviderId(providerId); // Einzelprovider-Analyse
    setShowCostDialog(true);
  };

  const handleAnalyzeSingleProviderConfirmed = async () => {
    if (!pendingAnalysisProviderId) return;

    // Sofortiges UI Update
    performOptimisticProviderUpdate([pendingAnalysisProviderId]);

    try {
      const result = await serviceProviderService.analyzeAllServiceProviders([pendingAnalysisProviderId]);
      if (result.status === 'success') {
        showNotification(t('providers:messages.analyzeSuccess'), 'success');
      } else {
        showNotification(result.message, 'error');
      }
    } catch (err) {
      showNotification(t('providers:messages.analyzeError', { error: err }), 'error');
    }
  };

  const handleDeleteSelected = async () => {
    try {
      const result = await serviceProviderService.deleteMultipleServiceProviders(selectedItems);
      if (result.status === 'success') {
        showNotification(t('providers:messages.deleteSuccess'), 'success');
        refreshAll();
        setSelectedItems([]);
      } else {
        showNotification(t('providers:messages.deleteError'), 'error');
      }
    } catch (err) {
      showNotification(t('providers:messages.deleteError', { error: err }), 'error');
    }
  };

  // Add Provider Dialog Handlers
  const handleAddProvider = () => {
    setIsAddProviderDialogOpen(true);
  };

  const handleSaveNewProvider = async () => {
    if (!newProviderName.trim()) {
      showNotification(t('providers:dialog.nameRequired'), 'error');
      return;
    }

    try {
      const result = await serviceProviderService.createServiceProvider(newProviderName.trim());
      if (result.status === 'success') {
        showNotification(t('providers:messages.createSuccess'), 'success');
        refreshAll();
      } else {
        showNotification(t('providers:messages.createError'), 'error');
      }
    } catch (error) {
      console.error('Error creating service provider:', error);
      showNotification(t('providers:messages.createError'), 'error');
    }

    setIsAddProviderDialogOpen(false);
    setNewProviderName('');
  };

  const handleCancelAddProvider = () => {
    setIsAddProviderDialogOpen(false);
    setNewProviderName('');
  };

  // Event-Listener für sofortige Provider-Updates
  useProviderUpdateListener(({ providerId, updates }) => {
    setServiceProviders(prevProviders =>
      prevProviders.map(provider =>
        provider._id === providerId
          ? { ...provider, ...updates } as ServiceProviderBase
          : provider
      )
    );
  });

  // Polling Handlers
  const handleProvidersUpdate = (newProviders: ServiceProviderBase[]) => {
    setServiceProviders(newProviders);
  };

  const handlePollingError = (error: string) => {
    console.error('Polling error:', error);
  };

  // === CONTEXT MENU ACTIONS ===
  const getContextMenuActions = (providerId: string, status: string): MenuAction[] => {
    return [
      {
        label: t('providers:contextMenu.open'),
        icon: <img src={IMAGES.NAVBAR_UBERSICHT} alt="Open" />,
        onClick: () => {
          navigate(`/dienstleister/${providerId}`);
          // navigate is not available here, but the table handles it.
          // This could be passed down if needed.
          handleCloseContextMenu();
        }
      },
      {
        label: t('providers:contextMenu.analyze'),
        icon: <img src={IMAGES.NAVBAR_UBERSICHT} alt="Analyze" />,
        onClick: () => {
          handleAnalyzeSingleProvider(providerId);
          handleCloseContextMenu();
        },
      },
      {
        label: t('providers:contextMenu.delete'),
        color: '#FF0004',
        icon: <img src={IMAGES.DELETE} alt="Delete" className="preserve-color" />,
        onClick: async () => {
          try {
            const result = await serviceProviderService.deleteServiceProvider(providerId);
            if (result.status === 'success') {
              showNotification(t('providers:messages.deleteSuccess'), 'success');
              await refreshServiceProviders();
            } else {
              showNotification(t('providers:messages.deleteError'), 'error');
            }
          } catch (err) {
            showNotification(t('providers:messages.deleteError', { error: err }), 'error');
          }
          handleCloseContextMenu();
        }
      }
    ];
  };

  // === EFFECTS ===
  useEffect(() => {
    const fetchServiceProviders = async () => {
      try {
        setLoading(true);
        const providers = await serviceProviderService.getServiceProviders();
        setServiceProviders(providers);
        setError(null);
      } catch (err) {
        setError(t('providers:messages.loadError'));
        console.error('Error loading service providers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceProviders();
  }, [t]);

  // === RENDER HELPER ===
  return (
    <GenericSidebar
      title={t('providers:sidebar.title')}
      titlePath="/dienstleister"
      isWide={isWide}
      onToggleWidth={onToggleWidth}
      loading={loading}
      error={error}
      filterControls={
        <FilterControls
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          onSearchChange={handleSearchChange}
          onStatusFilterChange={handleStatusFilterChange}
        />
      }
      tableContent={
        <ServiceProviderTable
          providers={serviceProviders}
          selectedItems={selectedItems}
          selectedRowId={selectedRowId}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          isWide={isWide}
          enablePolling={POLLING_CONFIG.ENABLE_POLLING}
          pollingInterval={POLLING_CONFIG.GLOBAL_INTERVAL}
          onSelectItem={handleSelectItem}
          onSelectAll={handleSelectAll}
          onContextMenu={handleContextMenu}
          onProvidersUpdate={handleProvidersUpdate}
          onError={handlePollingError}
        />
      }
      actionButtons={
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {selectedItems.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ fontSize: '0.875rem' }}>
                {t('providers:sidebar.selected', { count: selectedItems.length })}
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={handleAnalyzeSelected}
                sx={{ textTransform: 'none', flex: 1, }}
                data-testid="provider-sidebar-analyze-selected-button"
              >
                {t('providers:actions.analyze')}
              </Button>
              <Box sx={{ display: 'flex', gap: 1, paddingTop: 1 }}>
                <span
                  style={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'center',
                    gap: 4,
                    color: 'primary.dark',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem'
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', tabIndex: 0 }}
                    onClick={() => setSelectedItems([])}
                    data-testid="provider-sidebar-cancel-selection-button"
                  >
                    <CloseIcon sx={{ fontSize: '1.4rem' }} />
                    {t('common:buttons.cancel')}
                  </Box>
                </span>
                <span
                  style={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'center',
                    gap: 4,
                    color: 'primary.dark',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem'
                  }}
                >
                  <Box
                    onClick={handleDeleteSelected}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#FF0004',
                      fontWeight: 500,
                      fontSize: '1rem',
                      opacity: 1,
                      pointerEvents: 'auto'
                    }}
                    data-testid="provider-sidebar-delete-selected-button"
                  >
                    <img src={IMAGES.DELETE} alt="Delete" />
                    {t('common:buttons.delete')}
                  </Box>
                </span>
              </Box>
            </Box>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddProvider}
              sx={{ textTransform: 'none', width: '100%' }}
              data-testid="provider-sidebar-add-provider-button"
            >
              {t('providers:sidebar.addProvider')}
            </Button>
          )}
        </Box>
      }
      dialogs={
        <>
          <ContextMenu
            open={contextMenu !== null}
            onClose={handleCloseContextMenu}
            mouseX={contextMenu?.mouseX || 0}
            mouseY={contextMenu?.mouseY || 0}
            actions={contextMenu ? getContextMenuActions(contextMenu.providerId, contextMenu.status) : []}
          />
          <AddProviderDialog
            open={isAddProviderDialogOpen}
            newProviderName={newProviderName}
            onClose={handleCancelAddProvider}
            onSave={handleSaveNewProvider}
            onNameChange={setNewProviderName}
          />

          {/* Kostenbestätigung Dialog */}
          <AnalysisCostDialog
            open={showCostDialog}
            onClose={() => {
              setShowCostDialog(false);
              setPendingAnalysisProviderId(null);
            }}
            onConfirm={() => {
              setShowCostDialog(false);
              if (pendingAnalysisProviderId) {
                handleAnalyzeSingleProviderConfirmed();
              } else {
                handleAnalyzeSelectedConfirmed();
              }
              setPendingAnalysisProviderId(null);
            }}
            analysisType={currentAnalysisType}
            contractCount={currentContractCount}
            loading={currentCostEstimation.loading}
            error={currentCostEstimation.error}
            apiData={currentCostEstimation.data}
            creditInfo={creditInfo.isUnlimited ? null : {
              availableCredits: creditInfo.availableCredits,
              requiredCredits: currentCostEstimation.data?.totalAnalyzableContracts ?? currentContractCount,
              remainingAfter: creditInfo.availableCredits !== null
                ? creditInfo.availableCredits - (currentCostEstimation.data?.totalAnalyzableContracts ?? currentContractCount)
                : null
            }}
          />
        </>
      }
    />
  );
};

export default DienstleisterSidebar;
